export const isReadyThumbnail = (mediaAsset) => (
  (mediaAsset?.thumbnailGenerationStatus || mediaAsset?.thumbnail_generation_status) === 'ready'
);

export const pickUrl = (...candidates) => (
  candidates.find((candidate) => typeof candidate === 'string' && candidate.length > 0) || ''
);

const buildImageThumbnail = (url, source) => (
  url ? { kind: 'image', url, source } : null
);

const buildVideoThumbnail = (url, source) => (
  url ? { kind: 'video', url, source } : null
);

const buildImageThumbnailWithPlayback = (imageUrl, imageSource, playbackVideoUrl, playbackSource = 'mediaUrl') => {
  if (!imageUrl) return null;

  return {
    kind: 'image',
    url: imageUrl,
    source: imageSource,
    playbackVideoUrl: playbackVideoUrl || '',
    playbackSource: playbackVideoUrl ? playbackSource : '',
  };
};

const isImageUrl = (url) => (
  /^data:image\//.test(url)
  || /\.(avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i.test(url)
);

const isVideoUrl = (url) => (
  /^data:video\//.test(url)
  || /\.(m3u8|m4v|mov|mp4|mpeg|mpg|ogv|webm)(?:[?#].*)?$/i.test(url)
);

const getContentGroupMimeType = (contentGroup) => pickUrl(
  contentGroup?.attachmentData?.metadata?.mime_type,
  contentGroup?.attachment_data?.metadata?.mime_type
);

export const resolveContentGroupThumbnail = (contentGroup) => {
  if (!contentGroup) return null;

  const thumbnailImageUrl = isReadyThumbnail(contentGroup)
    ? pickUrl(contentGroup?.thumbnailImageUrl, contentGroup?.thumbnail_image_url)
    : '';
  const fixedUrl = pickUrl(contentGroup?.imageUrl, contentGroup?.image_url);
  const mimeType = getContentGroupMimeType(contentGroup);
  const hasVideoThumbnail = mimeType.includes('video') || isVideoUrl(fixedUrl);

  if (hasVideoThumbnail) {
    if (thumbnailImageUrl) {
      return buildImageThumbnailWithPlayback(
        thumbnailImageUrl,
        'contentGroup.thumbnailImageUrl',
        fixedUrl,
        'contentGroup.imageUrl'
      );
    }
    if (fixedUrl) {
      return buildVideoThumbnail(fixedUrl, 'contentGroup.imageUrl');
    }
  }

  if (thumbnailImageUrl) {
    return buildImageThumbnail(thumbnailImageUrl, 'contentGroup.thumbnailImageUrl');
  }

  if (fixedUrl && (mimeType.includes('image') || isImageUrl(fixedUrl))) {
    return buildImageThumbnail(fixedUrl, 'contentGroup.imageUrl');
  }

  return null;
};

export const resolveClosedCampaignThumbnail = (campaign, contentGroup, content) => {
  const frame = campaign?.frame || '';
  const mediaAsset = content?.mediaAsset || null;
  const mimeType = pickUrl(
    mediaAsset?.attachmentData?.metadata?.mime_type,
    mediaAsset?.attachment_data?.metadata?.mime_type
  );
  const mediaUrl = mediaAsset?.mediaUrl || '';
  const thumbnailImageUrl = isReadyThumbnail(mediaAsset)
    ? pickUrl(mediaAsset?.thumbnailImageUrl, mediaAsset?.thumbnail_image_url)
    : '';
  const contentGroupThumbnail = resolveContentGroupThumbnail(contentGroup);

  if (!frame || !content || !mimeType) return null;

  if (frame === 'circle') {
    if (contentGroupThumbnail) {
      // ストーリー型の非モーダルサムネは、グループ側のサムネ指定をコンテンツ動画で上書きしない
      return contentGroupThumbnail;
    }

    if (mimeType.includes('video')) {
      if (thumbnailImageUrl) {
        return buildImageThumbnailWithPlayback(thumbnailImageUrl, 'thumbnailImageUrl', mediaUrl);
      }
      return buildVideoThumbnail(mediaUrl, 'mediaUrl');
    }

    if (mimeType.includes('image')) {
      if (thumbnailImageUrl) {
        return buildImageThumbnail(thumbnailImageUrl, 'thumbnailImageUrl');
      }
      return buildImageThumbnail(mediaUrl, 'mediaUrl');
    }

    return null;
  }

  // リール型・タイル型は縦長サムネイルとして同一解決ロジックを使う
  if (frame === 'square' || frame === 'tile') {
    if (mimeType.includes('image')) {
      if (contentGroupThumbnail) {
        return contentGroupThumbnail;
      }
      return buildImageThumbnail(
        pickUrl(thumbnailImageUrl, mediaUrl),
        thumbnailImageUrl ? 'thumbnailImageUrl' : 'mediaUrl'
      );
    }

    if (mimeType.includes('video')) {
      if (contentGroupThumbnail) {
        return buildImageThumbnailWithPlayback(contentGroupThumbnail.url, contentGroupThumbnail.source, mediaUrl);
      }
      if (thumbnailImageUrl) {
        return buildImageThumbnailWithPlayback(thumbnailImageUrl, 'thumbnailImageUrl', mediaUrl);
      }
      return buildVideoThumbnail(mediaUrl, 'mediaUrl');
    }
  }

  return null;
};
